import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Loader2, Save, X } from "lucide-react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { STORAGE_KEY } from "../../lib/constants";

interface ProductDetailDrawerProps {
	productId: number;
	open: boolean;
	onClose: () => void;
}

export default function ProductDetailDrawer({ productId, open, onClose }: ProductDetailDrawerProps) {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState<any>(null);
	const [saving, setSaving] = useState(false);
	const [editedInfo, setEditedInfo] = useState<Record<string, string>>({});
	const [editedPrice, setEditedPrice] = useState<Record<string, string>>({});
	const { toast } = useToast();

	const getUserInfo = () => {
		const user = localStorage.getItem(STORAGE_KEY);
		if (!user) return { email: "unknown@pillplus.com" };
		const parsed = JSON.parse(user);
		return { email: parsed.email || "unknown@pillplus.com", name: parsed.displayName || parsed.name };
	};

	useEffect(() => {
		if (open && productId) fetchDetail();
	}, [open, productId]);

	const fetchDetail = async () => {
		setLoading(true);
		try {
			const res = await api.b2cMgmtGetProductDetail(productId);
			if (res?.success) {
				setData(res.data);
				setEditedInfo({});
				setEditedPrice({});
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to load product", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	const handleFlagToggle = async (flag: string, currentValue: boolean) => {
		const user = getUserInfo();
		try {
			const res = await api.b2cMgmtUpdateFlags(productId, { [flag]: !currentValue }, user);
			if (res?.success) {
				setData((prev: any) => ({
					...prev,
					product: { ...prev.product, [flag]: !currentValue },
				}));
				toast({ title: "Updated", description: res.message });
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to update flag", variant: "destructive" });
		}
	};

	const handleSaveInfo = async () => {
		if (Object.keys(editedInfo).length === 0) return;
		setSaving(true);
		const user = getUserInfo();
		try {
			const res = await api.b2cMgmtUpdateInfo(productId, { ...editedInfo, user });
			if (res?.success) {
				toast({ title: "Saved", description: res.message });
				fetchDetail();
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to save", variant: "destructive" });
		} finally {
			setSaving(false);
		}
	};

	const handleSavePrice = async () => {
		if (Object.keys(editedPrice).length === 0) return;
		setSaving(true);
		const user = getUserInfo();
		try {
			const payload: any = { user };
			if (editedPrice.mrp) payload.mrp = Number(editedPrice.mrp);
			if (editedPrice.current_price) payload.current_price = Number(editedPrice.current_price);
			const res = await api.b2cMgmtUpdatePrice(productId, payload);
			if (res?.success) {
				toast({ title: "Saved", description: res.message });
				fetchDetail();
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to save price", variant: "destructive" });
		} finally {
			setSaving(false);
		}
	};

	if (!open) return null;

	const product = data?.product;

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto dark:bg-gray-800 dark:border-gray-700">
				<DialogHeader>
					<DialogTitle className="dark:text-white">
						{loading ? "Loading..." : `${product?.product_name} (#${product?.id})`}
					</DialogTitle>
				</DialogHeader>

				{loading ? (
					<div className="flex items-center justify-center h-40">
						<Loader2 className="h-6 w-6 animate-spin text-blue-500" />
					</div>
				) : !data ? (
					<p className="text-gray-500 dark:text-gray-400">Product not found</p>
				) : (
					<Tabs defaultValue="info" className="mt-4">
						<TabsList className="dark:bg-gray-700">
							<TabsTrigger value="info">Info & Flags</TabsTrigger>
							<TabsTrigger value="pricing">Pricing</TabsTrigger>
							<TabsTrigger value="branches">Branches ({data.entity_products?.length || 0})</TabsTrigger>
							<TabsTrigger value="mrp_history">MRP History ({data.mrp_history?.length || 0})</TabsTrigger>
							<TabsTrigger value="changelog">Changes ({data.change_log?.length || 0})</TabsTrigger>
						</TabsList>

						{/* Info & Flags */}
						<TabsContent value="info" className="space-y-4">
							<Card className="dark:bg-gray-900 dark:border-gray-700">
								<CardHeader>
									<CardTitle className="text-sm dark:text-white">Product Info</CardTitle>
								</CardHeader>
								<CardContent className="space-y-3">
									<div className="grid grid-cols-2 gap-4">
										<div>
											<Label className="dark:text-gray-300">Product Name</Label>
											<Input
												defaultValue={product.product_name}
												onChange={(e) => setEditedInfo({ ...editedInfo, product_name: e.target.value })}
												className="dark:bg-gray-800 dark:border-gray-600"
											/>
										</div>
										<div>
											<Label className="dark:text-gray-300">Packing</Label>
											<Input
												defaultValue={product.packing || ""}
												onChange={(e) => setEditedInfo({ ...editedInfo, packing: e.target.value })}
												className="dark:bg-gray-800 dark:border-gray-600"
											/>
										</div>
										<div>
											<Label className="dark:text-gray-300">Packing Information</Label>
											<Input
												defaultValue={product.packing_information || ""}
												onChange={(e) => setEditedInfo({ ...editedInfo, packing_information: e.target.value })}
												className="dark:bg-gray-800 dark:border-gray-600"
											/>
										</div>
										<div>
											<Label className="dark:text-gray-300">Salt Composition</Label>
											<Input
												defaultValue={product.salt_composition || ""}
												onChange={(e) => setEditedInfo({ ...editedInfo, salt_composition: e.target.value })}
												className="dark:bg-gray-800 dark:border-gray-600"
											/>
										</div>
									</div>
									<div className="flex items-center gap-2 pt-2">
										<Label className="dark:text-gray-300">Company:</Label>
										<span className="text-sm dark:text-gray-400">{product.company_name || "—"}</span>
										<Label className="ml-4 dark:text-gray-300">MDM Category:</Label>
										<Badge>{product.mdm_category || "None"}</Badge>
										<Label className="ml-4 dark:text-gray-300">Medicine Type:</Label>
										<span className="text-sm dark:text-gray-400">{product.medicine_type || "—"}</span>
									</div>
									{Object.keys(editedInfo).length > 0 && (
										<Button onClick={handleSaveInfo} disabled={saving} size="sm">
											{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
											Save Info
										</Button>
									)}
								</CardContent>
							</Card>

							{/* Flags */}
							<Card className="dark:bg-gray-900 dark:border-gray-700">
								<CardHeader>
									<CardTitle className="text-sm dark:text-white">Flags</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
										{[
											{ key: "is_active", label: "Active" },
											{ key: "is_drug", label: "Drug" },
											{ key: "is_otc", label: "OTC" },
											{ key: "prescription_required", label: "Rx Required" },
											{ key: "is_discount_applicable", label: "Discount Applicable" },
											{ key: "banned_for_sale", label: "Banned For Sale" },
											{ key: "not_for_sale", label: "Not For Sale" },
											{ key: "discontinued", label: "Discontinued" },
											{ key: "out_of_stock", label: "Out of Stock" },
										].map((flag) => (
											<div key={flag.key} className="flex items-center justify-between p-2 rounded bg-gray-50 dark:bg-gray-800">
												<Label className="text-xs dark:text-gray-300">{flag.label}</Label>
												<Switch
													checked={product[flag.key]}
													onCheckedChange={() => handleFlagToggle(flag.key, product[flag.key])}
												/>
											</div>
										))}
									</div>
								</CardContent>
							</Card>

							{/* Categories & Tags */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<Card className="dark:bg-gray-900 dark:border-gray-700">
									<CardHeader>
										<CardTitle className="text-sm dark:text-white">Categories ({data.categories?.length || 0})</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="flex flex-wrap gap-1">
											{data.categories?.length > 0 ? data.categories.map((cat: any) => (
												<Badge key={cat.link_id} variant="secondary" className="text-xs">
													{cat.category_name}
													{cat.is_primary && " ★"}
												</Badge>
											)) : (
												<span className="text-xs text-gray-400 dark:text-gray-500">No categories assigned</span>
											)}
										</div>
									</CardContent>
								</Card>
								<Card className="dark:bg-gray-900 dark:border-gray-700">
									<CardHeader>
										<CardTitle className="text-sm dark:text-white">Tags ({data.tags?.length || 0})</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="flex flex-wrap gap-1">
											{data.tags?.length > 0 ? data.tags.map((tag: any) => (
												<Badge key={tag.link_id} variant="outline" className="text-xs">
													{tag.tag_name}
												</Badge>
											)) : (
												<span className="text-xs text-gray-400 dark:text-gray-500">No tags assigned</span>
											)}
										</div>
									</CardContent>
								</Card>
							</div>
						</TabsContent>

						{/* Pricing */}
						<TabsContent value="pricing" className="space-y-4">
							<Card className="dark:bg-gray-900 dark:border-gray-700">
								<CardHeader>
									<CardTitle className="text-sm dark:text-white">Price Correction</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="grid grid-cols-2 gap-4">
										<div>
											<Label className="dark:text-gray-300">MRP (₹)</Label>
											<Input
												type="number"
												defaultValue={product.mrp || ""}
												onChange={(e) => setEditedPrice({ ...editedPrice, mrp: e.target.value })}
												className="dark:bg-gray-800 dark:border-gray-600"
											/>
										</div>
										<div>
											<Label className="dark:text-gray-300">Current Price (₹)</Label>
											<Input
												type="number"
												defaultValue={product.current_price || ""}
												onChange={(e) => setEditedPrice({ ...editedPrice, current_price: e.target.value })}
												className="dark:bg-gray-800 dark:border-gray-600"
											/>
										</div>
									</div>
									<div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded text-sm dark:text-gray-300">
										{product.is_discount_applicable ? (
											product.mdm_category === "Generic" ? (
												<span>Discount active: Generic → flat 20% off → ₹{(Number(product.mrp) * 0.8).toFixed(2)}</span>
											) : (
												<span>Discount applicable but no auto-rule for "{product.mdm_category}" category</span>
											)
										) : (
											<span>No discount. Price = MRP</span>
										)}
									</div>
									{Object.keys(editedPrice).length > 0 && (
										<Button onClick={handleSavePrice} disabled={saving} size="sm" className="mt-3">
											{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
											Save Price
										</Button>
									)}
								</CardContent>
							</Card>
						</TabsContent>

						{/* Linked Branches */}
						<TabsContent value="branches">
							<Card className="dark:bg-gray-900 dark:border-gray-700">
								<CardHeader>
									<CardTitle className="text-sm dark:text-white">
										Linked med_entity_products (MRP: ₹{Number(product.mrp).toFixed(2)})
									</CardTitle>
								</CardHeader>
								<CardContent>
									{data.entity_products?.length > 0 ? (
										<div className="overflow-x-auto">
											<table className="w-full text-sm">
												<thead>
													<tr className="border-b dark:border-gray-700">
														<th className="py-2 px-3 text-left dark:text-gray-300">Branch</th>
														<th className="py-2 px-3 text-left dark:text-gray-300">SP Name</th>
														<th className="py-2 px-3 text-left dark:text-gray-300">SP Code</th>
														<th className="py-2 px-3 text-right dark:text-gray-300">SP MRP</th>
														<th className="py-2 px-3 text-right dark:text-gray-300">MRP Diff</th>
														<th className="py-2 px-3 text-left dark:text-gray-300">Pack</th>
														<th className="py-2 px-3 text-left dark:text-gray-300">Manufacturer</th>
													</tr>
												</thead>
												<tbody>
													{data.entity_products.map((ep: any) => {
														const medMrp = Number(product.mrp) || 0;
														const spMrp = Number(ep.mrp) || 0;
														const diff = spMrp - medMrp;
														return (
															<tr key={ep.id} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50">
																<td className="py-2 px-3 font-medium dark:text-gray-200">{ep.branch_name || ep.branch_id}</td>
																<td className="py-2 px-3 dark:text-gray-300 max-w-[200px] truncate">{ep.name}</td>
																<td className="py-2 px-3 font-mono text-xs dark:text-gray-300">{ep.code}</td>
																<td className="py-2 px-3 text-right font-mono dark:text-gray-200">₹{spMrp.toFixed(2)}</td>
																<td className={`py-2 px-3 text-right font-mono ${diff > 0 ? "text-green-600" : diff < 0 ? "text-red-600" : "text-gray-400"}`}>
																	{diff > 0 ? "+" : ""}{diff.toFixed(2)}
																</td>
																<td className="py-2 px-3 text-xs dark:text-gray-300">{ep.pack}</td>
																<td className="py-2 px-3 text-xs dark:text-gray-300 max-w-[150px] truncate">{ep.mfr}</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</div>
									) : (
										<p className="text-gray-400 dark:text-gray-500 text-sm">No branch products linked</p>
									)}
								</CardContent>
							</Card>
						</TabsContent>

						{/* MRP History */}
						<TabsContent value="mrp_history">
							<Card className="dark:bg-gray-900 dark:border-gray-700">
								<CardHeader>
									<CardTitle className="text-sm dark:text-white">MRP Timeline</CardTitle>
								</CardHeader>
								<CardContent>
									{data.mrp_history?.length > 0 ? (
										<div className="overflow-x-auto">
											<table className="w-full text-sm">
												<thead>
													<tr className="border-b dark:border-gray-700">
														<th className="py-2 px-3 text-left dark:text-gray-300">Date</th>
														<th className="py-2 px-3 text-right dark:text-gray-300">Old MRP</th>
														<th className="py-2 px-3 text-right dark:text-gray-300">New MRP</th>
														<th className="py-2 px-3 text-right dark:text-gray-300">Old Price</th>
														<th className="py-2 px-3 text-right dark:text-gray-300">New Price</th>
														<th className="py-2 px-3 text-left dark:text-gray-300">Source</th>
														<th className="py-2 px-3 text-left dark:text-gray-300">Changed By</th>
													</tr>
												</thead>
												<tbody>
													{data.mrp_history.map((h: any) => (
														<tr key={h.id} className="border-b dark:border-gray-700/50">
															<td className="py-2 px-3 text-xs dark:text-gray-300">{new Date(h.created_at).toLocaleString()}</td>
															<td className="py-2 px-3 text-right font-mono dark:text-gray-300">₹{Number(h.old_mrp || 0).toFixed(2)}</td>
															<td className="py-2 px-3 text-right font-mono font-medium dark:text-gray-200">₹{Number(h.new_mrp).toFixed(2)}</td>
															<td className="py-2 px-3 text-right font-mono dark:text-gray-300">₹{Number(h.old_current_price || 0).toFixed(2)}</td>
															<td className="py-2 px-3 text-right font-mono dark:text-gray-200">₹{Number(h.new_current_price || 0).toFixed(2)}</td>
															<td className="py-2 px-3">
																<Badge variant="outline" className="text-xs">{h.source}</Badge>
															</td>
															<td className="py-2 px-3 text-xs dark:text-gray-400">{h.changed_by_email || "system"}</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									) : (
										<p className="text-gray-400 dark:text-gray-500 text-sm">No MRP history available</p>
									)}
								</CardContent>
							</Card>
						</TabsContent>

						{/* Change Log */}
						<TabsContent value="changelog">
							<Card className="dark:bg-gray-900 dark:border-gray-700">
								<CardHeader>
									<CardTitle className="text-sm dark:text-white">Recent Changes</CardTitle>
								</CardHeader>
								<CardContent>
									{data.change_log?.length > 0 ? (
										<div className="space-y-2">
											{data.change_log.map((log: any) => (
												<div key={log.id} className="flex items-start gap-3 py-2 border-b dark:border-gray-700/50 last:border-0">
													<div className="flex-1">
														<div className="text-sm dark:text-gray-200">
															<Badge variant="outline" className="text-xs mr-2">{log.change_type}</Badge>
															<span className="font-mono text-xs">{log.field_name}</span>
															{log.old_value && (
																<span className="text-gray-400"> {log.old_value} → </span>
															)}
															{log.new_value && (
																<span className="font-medium">{log.new_value}</span>
															)}
														</div>
														<div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
															by {log.changed_by_name || log.changed_by_email}
															{log.reason && ` — "${log.reason}"`}
														</div>
													</div>
													<div className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
														{new Date(log.created_at).toLocaleString()}
													</div>
												</div>
											))}
										</div>
									) : (
										<p className="text-gray-400 dark:text-gray-500 text-sm">No changes recorded</p>
									)}
								</CardContent>
							</Card>
						</TabsContent>
					</Tabs>
				)}
			</DialogContent>
		</Dialog>
	);
}
